import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { Price } from '@adornis/payments/db/prices.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { DateTime } from 'luxon';
import { BHInvoice } from '../../entities/BHInvoice.js';
import { BHPayment } from '../../entities/BHPayment.js';
import { Bundle } from '../../entities/Bundle.js';
import { Participation } from '../../entities/Participation.js';
import { Einzelberatung } from '../../entities/einzelberatung.js';
import {
  getEmailVarsFromEinzelberatung,
  getEmailVarsFromUser,
  getEmailVarsFromWorkshop,
} from '../../entities/mail-helper.js';
import { getWhatsappTemplate, InstitutionMailTemplate, MailTemplateType } from '../../entities/mail-templates.js';
import { Product } from '../../entities/products.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import { User } from '../../entities/user.js';
import { Workshop } from '../../entities/workshop.js';
import { ParticipationState } from '../../enums/enums.js';
import { getProductsBestBundled } from '../../global-helpers.js';
import { sendInstitutionEmail } from '../../mails/sendInstitutionMail.js';
import { notifyBestaetigungWorkshopRegistrierung } from '../../notification/bestaetigung-workshop-registrierung.js';
import { printComponent } from '../../print-with-user-session.js';
import { isDefined } from '../../util.js';
import type { PaymentPlugin } from '../plugin-types.js';
import { IKHInvoiceReceiverEnum, IKHStammdaten } from '../stammdaten/ikh-stammdaten-plugin.js';
import { BHIKHOrderNr } from './BHIKHOrderNr.js';
import { defaultPaymentPlugin } from './default.js';
import { createPaymentFromParticipations } from './helper.js';

export const ikhGenerateInvoice = registerMutation({
  type: () => Int,
  operationName: 'ikhGenerateInvoice',
  params: [
    { type: () => String, name: 'paymentID' },
    { type: () => Boolean, name: 'invoiceCorrection' },
  ],
  resolve: (paymentID: string, invoiceCorrection: boolean) => {
    return async () => {
      const payment = await getByID(
        BHPayment,
        paymentID,
      )({ _id: 1, participations: { _id: 1, userID: 1, product: { _id: 1, institutionID: 1 } } });
      const userID = payment?.participations[0]?.userID;
      if (!userID) throw new Error('userID not found');
      const institutionID = payment?.participations[0]?.product?.institutionID;
      if (!institutionID) throw new Error('institutionID not found');

      const user = await getByID(User, userID)(User.allFields);
      if (!user) throw new Error('user not found');

      const existingInvoice = await loadMongoData(IKHInvoice, { paymentID }).then(its => its[0]);
      const invoiceID = existingInvoice?._id ?? A.getGloballyUniqueID();
      const createInvoice = async (component: string, filenameType: string) => {
        const buffer = await printComponent(component, {
          user,
          timeout: 25 * 1000,
          queryParams: { payment: paymentID, isCorrection: invoiceCorrection },
        });

        const correctionStr = invoiceCorrection ? 'correction-' : '';
        const dateStr = DateTime.now().toFormat('yyyy-LL-mm_HH-mm');
        return await AdornisFile.createFromBuffer(
          buffer,
          `invoice-ikh-${filenameType}-${invoiceID}-${correctionStr}${dateStr}.pdf`,
        );
      };

      const invoiceCustomer = await createInvoice('ikh-invoice-customer', 'customer');
      const invoiceInternal = await createInvoice('ikh-invoice-internal', 'internal');

      if (invoiceCorrection) {
        if (!existingInvoice)
          throw new Error(
            'no existing invoice found; can only create invoice-correction if normal invoice already exists',
          );
        const invoice = new IKHInvoice(existingInvoice);
        invoice.pdfCorrectionID = invoiceCustomer._id;
        invoice.pdfCorrectionInternalID = invoiceInternal._id;
        invoice.save();
        return;
      }

      const invoice = new IKHInvoice({
        _id: invoiceID,
        paymentID,
        pdfID: invoiceCustomer._id,
        pdfInternalID: invoiceInternal._id,
        userID: user._id,
        institutionID,
      });

      if (!!existingInvoice) await invoice.save();
      else await invoice.create();
    };
  },
});

async function notifiyIkhInvoice(
  institutionID: string,
  user: User,
  product: Product,
  template: MailTemplateType,
  invoiceLink: string,
  receiverMail: string,
  cc: Maybe<string>,
) {
  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    template,
  )(InstitutionMailTemplate.allFields);

  if (user && user.whatsappNotification && user.whatsappPhone && mailTemplate.whatsappNotification) {
    try {
      const whatsapp_template = getWhatsappTemplate(template);
      const { getWhatsapp } = await import('../../whatsapp/util.js');
      const whatsapp = getWhatsapp();
      const response = await whatsapp.sendMessage({
        to: user.whatsappPhone,
        type: MessageType.TEMPLATE,
        content: {
          template: {
            name: whatsapp_template.name,
            language: {
              code: 'de',
            },
            components: [
              {
                type: ComponentType.HEADER,
                parameters: [
                  {
                    type: ComponentParamaterType.IMAGE,
                    image: {
                      link: A.absoluteUrl(invoiceLink + '/Rechnung.pdf'),
                    },
                  },
                ],
              },
              {
                type: ComponentType.BODY,
                parameters: [
                  {
                    type: ComponentParamaterType.TEXT,
                    text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                  },
                  {
                    type: ComponentParamaterType.TEXT,
                    text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                  },
                ],
              },
            ],
          },
        },
      });
      logger.info({ response }, 'whatsapp result');
    } catch (e) {
      logger.error({ err: e }, 'Error sending whatsapp message');
    }
  }
  if (!mailTemplate.emailNotification || mailTemplate.whatsappNotification) return false;

  let emailVars = (() => {
    if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
    else if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
    else return getEmailVarsFromUser(user);
  })();

  const mailContent = await mailTemplate.buildEmail(emailVars);

  if (!mailContent) throw new Error('could not build email');
  await sendInstitutionEmail({
    email: receiverMail,
    subject: mailTemplate.subject,
    content: mailContent,
    institutionID,
    attachmentPaths: [A.absoluteUrl(invoiceLink + '/Rechnung.pdf')],
    cc,
  });
}

export const sendIKHInvoiceMails = registerMutation({
  type: () => Boolean,
  operationName: 'sendIKHInvoiceMails',
  params: [
    { type: () => String, name: 'invoiceID' },
    { type: () => Boolean, name: 'isInvoiceCorrection' },
  ],
  resolve: (invoiceID: string, isInvoiceCorrection: boolean) => {
    return async () => {
      const invoice = await getByID(
        IKHInvoice,
        invoiceID,
      )({ _id: 1, paymentID: 1, pdfID: 1, pdfInternalID: 1, pdfCorrectionID: 1, pdfCorrectionInternalID: 1 });
      if (!invoice) throw new Error('invoice not found');

      const payment = await getByID(
        BHPayment,
        invoice.paymentID,
      )({
        _id: 1,
        createdAt: 1,
        participationIDs: 1,
        status: 1,
        orderNr: { year: 1, nr: 1 },
      });
      if (!payment) throw new Error('should not happen');

      const participations = await Promise.all(
        payment.participationIDs.map(id =>
          getByID(
            Participation,
            id,
          )({
            _id: 1,
            userID: 1,
            user: {
              _id: 1,
              email: 1,
              firstName: 1,
              lastName: 1,
              title: 1,
              username: 1,
              isLdapAccount: 1,
              whatsappNotification: 1,
              whatsappPhone: 1,
              suspentions: { _id: 1, institutionID: 1 },
              receivesNewsletter: 1,
            },
            productID: 1,
            createdAt: 1,
            updatedAt: 1,
            state: 1,
            isNoShow: 1,
            signoffDate: 1,
            registrationDate: 1,
            fulfilledParticipationRequirements: 1,
            lastStateChangeWasInitiatedByAdmin: 1,
            currentPayment: {
              status: 1,
              participationIDs: 1,
              orderNr: { year: 1, nr: 1 },
              amount: 1,
              discount: 1,
            },
            productPriceForUser: { unitAmount: 1, currency: 1 },
            product: {
              _class: 1,
              _id: 1,
              name: 1,
              description: 1,
              translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
              sortingWeight: 1,
              hasPrice: 1,
              categoryID: 1,
              institutionID: 1,
              timeframeID: 1,
              attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
              lastReminderMailSent: 1,
              publishDate: 1,
              topic: 1,
              language: 1,
              presenceType: 1,
              isCanceled: 1,
              appointments: {
                _class: 1,
                _id: 1,
                start: 1,
                end: 1,
                isNonBlocking: 1,
                location: 1,
                name: 1,
              },
              capacity: 1,

              hideCapacity: 1,
            },
          }),
        ),
      ).then(ps => ps.filter(isDefined));

      const p = participations[0];
      if (!p) throw new Error('participations required');

      if (p.state === ParticipationState.WAITING) {
        logger.debug('invoice not send because user is on waiting list');
        return false;
      }

      const user = p.user;
      if (!user) throw new Error('user is missing');

      const product = p.product;
      if (!product) throw new Error('product not found');

      const institutionID = product.institutionID;

      const collection = await getCollection<IKHStammdaten>(UserStammdaten._class);
      const stammdaten = await collection.findOne({ userID: user._id, institutionID });
      if (!stammdaten) throw new Error('stammdaten not found');

      const invoiceCustomerLink = AdornisFile.getServeLink(
        isInvoiceCorrection ? invoice.pdfCorrectionID : invoice.pdfID,
      );
      const invoiceinternalLink = AdornisFile.getServeLink(
        isInvoiceCorrection ? invoice.pdfCorrectionInternalID : invoice.pdfInternalID,
      );
      const sendUserMail = (userMail: string, cc: Maybe<string> = null) =>
        notifiyIkhInvoice(
          institutionID,
          user,
          product,
          MailTemplateType.IKH_INVOICE_USER,
          invoiceCustomerLink,
          userMail,
          cc,
        );
      const sendInstitutionMail = (institutionMail: string) =>
        notifiyIkhInvoice(
          institutionID,
          user,
          product,
          MailTemplateType.IKH_INVOICE_INSTITUTION,
          invoiceinternalLink,
          institutionMail,
          null,
        );

      const isIntern = (stammdaten as any).invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL;
      const isExtern = (stammdaten as any).invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL;

      // user mail
      if (isIntern) void 0; // do nothing
      else if (isExtern) await sendUserMail(user.email, stammdaten.institutionEmail);
      else await sendUserMail(user.email);

      // institution mail
      await (async () => {
        const s = p.product?.settings;
        if (!s) {
          logger.warn('no settings found; cant send mail');
          return;
        }
        const institutionMail1 = s.interAccountingEmail1 ?? s.inheritedInterAccountingEmail1?.[1];
        const institutionMail2 = s.interAccountingEmail2 ?? s.inheritedInterAccountingEmail2?.[1];
        const institutionMail3 = s.interAccountingEmail3 ?? s.inheritedInterAccountingEmail3?.[1];
        const institutionMails = [institutionMail1, institutionMail2, institutionMail3]
          .filter(it => !!it)
          .filter(isDefined);
        if (institutionMails.length === 0) {
          logger.warn('no institution-mail available; cant send institution-invoice-copy');
          return;
        }

        for (const institutionMail of institutionMails) {
          await sendInstitutionMail(institutionMail);
        }
      })();

      if (!isInvoiceCorrection) {
        payment.status = 'issued';
        await payment.save();
      }

      return true;
    };
  },
});

@Entity()
export class IKHInvoice extends BHInvoice {
  static override _class = 'IKHInvoice';

  // pdf file path of interal-invoice version for accouning
  @Field(type => String)
  pdfInternalID!: string;

  @Field(type => String)
  pdfCorrectionID!: string;

  @Field(type => String)
  pdfCorrectionInternalID!: string;
}

const getProductPrice = async (productID: string, userID: string) => {
  const product = await getByID(
    Product,
    productID,
  )({ prices: { _id: 1, unitAmount: 1, currency: 1, ...Price.allFields } });
  if (!product) throw new Error('could not find product ' + productID);
  return product?.prices?.[0] as Maybe<Price>;
};

const paymentPluginIkhPay = registerMutation({
  type: () => [String],
  operationName: 'paymentPluginIkhPay',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [String], name: 'participationIds' },
  ],
  resolve: (institutionID: string, participationIDs: string[]) => {
    return async () => {
      const participations = (await getByID(
        Participation,
        participationIDs,
      )({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          isLdapAccount: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: {
          _id: 1,
          name: 1,
          institutionID: 1,
          hasPrice: 1,
          prices: { unitAmount: 1, currency: 1 },
        },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: {
          status: 1,
          participationIDs: 1,
          orderNr: { year: 1, nr: 1 },
          amount: 1,
          discount: 1,
        },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      })) as Participation[];
      const availableBundles = await Bundle.getAllByInstitution(institutionID)(Bundle.allBillFields);

      const user = await CurrentUserInfo.getMyself<User>()({
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
      });
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await UserStammdaten.getByUserAndInstitution(user._id, institutionID)(IKHStammdaten.allFields);
      if (!stammdaten) throw new Error('stammdaten not found userID=' + user._id + ', institutionID=' + institutionID);

      // send of mail per participation
      const bestBundling = getProductsBestBundled(
        participations.map(pa => pa.product!).filter(p => !!p),
        availableBundles,
      );

      const paymentIDs = [] as string[];
      for (const curBooked of bestBundling) {
        let curBillParti: Participation[];
        if (curBooked instanceof Product) curBillParti = [participations.find(pa => pa.productID === curBooked._id)!];
        else curBillParti = participations.filter(pa => curBooked.products.find(p => p._id === pa.productID));

        const payment = await createPaymentFromParticipations(institutionID, curBillParti, getProductPrice);
        (payment.orderNr = await BHIKHOrderNr.create(institutionID)), (payment.userReference.userID = user._id);

        const paymentID = await payment.create();
        logger.info({ paymentID }, '(ikh) payment created');
        paymentIDs.push(paymentID);

        // create invoice
        await ikhGenerateInvoice(paymentID, false)();
      }

      // send Mails
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);

      return paymentIDs;
    };
  },
});

export const ikhPaymentPlugin: PaymentPlugin<'invoice', 'default'> = {
  id: 'payment::ikh',
  type: 'payment',
  paymentMethods: ['invoice'],
  getProductPrice,
  createPayment: async (participationIDs: string[], _paymentMethod) => {
    const user = await CurrentUserInfo.getMyself()({});
    if (!user) throw new Error('user not found');

    const participations = await getByID(
      Participation,
      participationIDs,
    )({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: {
        _id: 1,
        name: 1,
        institutionID: 1,
        hasPrice: 1,
        prices: { unitAmount: 1, currency: 1 },
      },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: {
        status: 1,
        participationIDs: 1,
        orderNr: { year: 1, nr: 1 },
        amount: 1,
        discount: 1,
      },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const institutionID = participations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    return await paymentPluginIkhPay(institutionID, participationIDs)();
  },
  pay: async (paymentID, _paymentMethod) => {
    // nothing to do here; invoice gets paid manually by user
  },
  sendInvoice: async (invoiceID: string) => {
    // NOTE: deactivate invoice for now
    return;

    await sendIKHInvoiceMails(invoiceID, false)();
  },

  afterEnrollment: async participationID => {},

  cancleEnrollment: async (participationID: string) => {
    const participation = getByID(Participation, participationID)({ currentPayment: BHPayment.allFields });
    if (!participation.currentPayment) return;
    const payment = participation.currentPayment;
    switch (payment.status) {
      case 'paid':
        payment.state = 'refunded';
        await payment.save();
        return;
      default:
        await defaultPaymentPlugin.cancleEnrollment(participationID);
        return;
    }
  },

  priceGroups: ['default'],
};

export function testIkhPaymentPlugin() {}
